import React, { useEffect, useMemo, useState } from "react";
import { MdAdd, MdDelete, MdSearch } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import {
  getCampusSectionsStorageKey,
  normalizeSectionName,
} from "../../../utils/campusSections";
import { confirmDialog } from "../../../utils/dialogs";
import { C, Card, CardHeader, TH, TD, Badge, Btn } from "../../shared/fees/feeUI";
import { StatCard, StatGrid, BackButton } from "../../shared/reports/reportsShared";

function SectionsManagement() {
  const navigate = useNavigate();
  const { campusId } = useParams();

  const STORAGE_KEY = getCampusSectionsStorageKey(campusId);

  const [sections, setSections] = useState([]);
  const [sectionName, setSectionName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [loadedStorageKey, setLoadedStorageKey] = useState("");

  useEffect(() => {
    try {
      const savedSections = localStorage.getItem(STORAGE_KEY);
      const parsedSections = savedSections ? JSON.parse(savedSections) : [];
      setSections(Array.isArray(parsedSections) ? parsedSections : []);
    } catch (error) {
      console.error("Failed to load sections", error);
      setSections([]);
    } finally {
      setLoadedStorageKey(STORAGE_KEY);
    }
  }, [STORAGE_KEY]);

  useEffect(() => {
    if (loadedStorageKey !== STORAGE_KEY) return;

    localStorage.setItem(STORAGE_KEY, JSON.stringify(sections));
    window.dispatchEvent(
      new CustomEvent("campus-sections-updated", {
        detail: { campusId },
      }),
    );
  }, [campusId, loadedStorageKey, sections, STORAGE_KEY]);

  const createSection = () => {
    const normalizedSectionName = normalizeSectionName(sectionName);

    if (!normalizedSectionName) {
      alert("Please enter a section.");
      return;
    }

    const exists = sections.some(
      (s) => normalizeSectionName(s.section_name) === normalizedSectionName,
    );

    if (exists) {
      alert("Section already exists.");
      return;
    }

    const newSection = {
      id: Date.now(),
      section_name: normalizedSectionName,
    };

    setSections((prev) => [...prev, newSection]);
    setSectionName("");
    setShowAddSectionModal(false);
  };

  const deleteSection = async (id) => {
    if (
      !(await confirmDialog("Delete this section?", {
        title: "Delete Section",
        confirmLabel: "Delete Section",
        variant: "danger",
      }))
    ) {
      return;
    }
    setSections((prev) => prev.filter((s) => s.id !== id));
  };

  const filteredSections = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return sections.filter((section) =>
      normalizedSearch
        ? String(section.section_name || "").toLowerCase().includes(normalizedSearch)
        : true,
    );
  }, [searchTerm, sections]);

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: "24px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap", marginBottom: "20px" }}>
        <BackButton onClick={() => navigate(-1)} label="Back" />
        <div style={{ marginLeft: "auto" }}>
          
        </div>
      </div>

      <StatGrid>
        <StatCard label="Total" value={sections.length} accent={C.navy} />
        <StatCard label="Created" value={sections.length} accent={C.green} />
        <StatCard label="Showing" value={filteredSections.length} accent={C.blue} />
      </StatGrid>

      <Card style={{ marginBottom: "16px" }}>
        <div style={{ padding: "16px 20px", display: "grid", gridTemplateColumns: "1fr auto", gap: "16px", alignItems: "end" }}>
          <div>
            <label style={{ display: "block", fontSize: "11px", fontWeight: 700, color: C.slate, marginBottom: "5px", textTransform: "uppercase", letterSpacing: "0.4px" }}>
              Search
            </label>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: C.slateLight, display: "flex", pointerEvents: "none" }}>
                <MdSearch />
              </span>
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search sections"
                style={{ padding: "9px 13px 9px 36px", borderRadius: "8px", border: `1.5px solid ${C.border}`, fontSize: "13px", color: C.text, background: C.white, outline: "none", width: "100%", boxSizing: "border-box" }}
              />
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Btn onClick={() => setShowAddSectionModal(true)}>
              <MdAdd /> Add Section
            </Btn>
          </div>
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Sections"
          right={<Badge color="slate">{filteredSections.length} of {sections.length} shown</Badge>}
        />

        {filteredSections.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <TH>Section</TH>
                  <TH right>Actions</TH>
                </tr>
              </thead>
              <tbody>
                {filteredSections.map((section, i) => (
                  <tr key={section.id} style={{ background: i % 2 === 0 ? C.white : C.bg }}>
                    <TD>
                      <Badge color="blue">Section {section.section_name}</Badge>
                    </TD>
                    <TD right>
                      <Btn
                        size="sm"
                        variant="danger"
                        onClick={() => deleteSection(section.id)}
                      >
                        <MdDelete /> Delete
                      </Btn>
                    </TD>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {sections.length === 0 && (
          <div style={{ padding: "70px 20px", textAlign: "center", color: C.slate }}>
            <div style={{ fontWeight: 700, fontSize: "15px", color: C.text, marginBottom: "6px" }}>No sections yet</div>
            <p style={{ fontSize: "13px" }}>Create the first section for this campus.</p>
          </div>
        )}

        {sections.length > 0 && filteredSections.length === 0 && (
          <div style={{ padding: "70px 20px", textAlign: "center", color: C.slate }}>
            <div style={{ fontWeight: 700, fontSize: "15px", color: C.text, marginBottom: "6px" }}>No matching sections</div>
            <p style={{ fontSize: "13px" }}>Try a different search.</p>
          </div>
        )}
      </Card>

      {showAddSectionModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add New Section</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowAddSectionModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: "block", fontSize: "11px", fontWeight: 700, color: C.slate, marginBottom: "5px", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                  Section Name
                </label>
                <input
                  type="text"
                  value={sectionName}
                  onChange={(e) => setSectionName(e.target.value.toUpperCase())}
                  placeholder="Enter section name"
                  style={{ padding: "9px 13px", borderRadius: "8px", border: `1.5px solid ${C.border}`, fontSize: "13px", color: C.text, background: C.white, outline: "none", width: "100%", boxSizing: "border-box" }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      createSection();
                    }
                  }}
                  autoFocus
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowAddSectionModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={createSection}
                >
                  Create Section
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SectionsManagement;